/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import { Button, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import { getUrl } from 'bundles/cds-labs/components/Modules/utils/utm';
import type { TrackingProps } from 'bundles/page/components/TrackedLink2';

type Props = ButtonProps &
  TrackingProps & {
    onClick?: () => void;
  };

const styles = {
  button: css`
    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,

  secondaryButton: css`
    text-align: center;

    &:hover,
    &:active,
    &:visited {
      text-decoration: none;
    }
  `,
};

/**
 * This button wraps a CDS button and adds tracking while preserving UTM parameters from the href
 */
const PreservedUtmButton: React.FC<Props> = ({ children, href, variant, trackingName, onClick, ...otherProps }) => {
  const router = useRouter();
  const retrackedTrack = useRetracked();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (trackingName) {
      retrackedTrack({
        trackingName,
        trackingData: {},
        action: 'click',
      });
    }

    onClick?.();
  };

  const isGhostButton = variant === 'ghost' || variant === 'ghostInvert';

  return (
    <Button
      className="ContentfulButton"
      component="a"
      href={getUrl(router, href)}
      variant={variant}
      onClick={handleClick}
      css={[styles.button, isGhostButton ? null : styles.secondaryButton]}
      {...otherProps}
    >
      {children}
    </Button>
  );
};

export default PreservedUtmButton;
